import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Checkbox, CircularProgress, IconButton, Typography } from '@mui/material';

import {
  BLACK,
  DARKER_GREEN,
  FONT_FAMILY,
  NEUTRAL_600,
  NEUTRAL_BORDER,
  PRIMARY_TEAL
} from './colors';

import type { PublicClassification } from './types';

interface ClassificationsPanelProps {
  rootClassifications: PublicClassification[];
  childrenByRootId: Record<number, PublicClassification[]>;
  expandedRootIds: Set<number>;
  selectedId: number | null;
  expandingRootId: number | null;
  onToggleExpand: (rootId: number) => void;
  onToggleSelect: (id: number) => void;
}

interface ClassificationRowProps {
  classification: PublicClassification;
  indent?: boolean;
  isSelected: boolean;
  onToggleSelect: () => void;
}

const ClassificationRow = ({
  classification,
  indent,
  isSelected,
  onToggleSelect
}: ClassificationRowProps) => (
  <Box
    component="a"
    href={classification.url}
    onClick={(event) => {
      event.preventDefault();
      onToggleSelect();
    }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      textDecoration: 'none',
      paddingLeft: indent ? '28px' : 0
    }}
  >
    <Checkbox
      checked={isSelected}
      size="small"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggleSelect();
      }}
      sx={{
        padding: 0,
        color: NEUTRAL_BORDER,
        '&.Mui-checked': { color: PRIMARY_TEAL }
      }}
    />
    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '16px', lineHeight: 1.3, color: BLACK }}>
      {classification.label}
    </Typography>
  </Box>
);

const ClassificationsPanel = ({
  rootClassifications,
  childrenByRootId,
  expandedRootIds,
  selectedId,
  expandingRootId,
  onToggleExpand,
  onToggleSelect
}: ClassificationsPanelProps) => (
  <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '24px 32px' }}>
    <Typography
      sx={{
        fontFamily: FONT_FAMILY,
        fontWeight: 800,
        fontSize: '20px',
        color: '#033634',
        mb: '16px'
      }}
    >
      Classifications
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {rootClassifications.map((root) => {
        const isExpanded = expandedRootIds.has(root.id);
        const children = childrenByRootId[root.id];
        const isExpandLoading = expandingRootId === root.id && !children;
        const isChildSelected = children?.some((child) => child.id === selectedId) ?? false;

        return (
          <Box key={root.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Box sx={{ flex: 1 }}>
                <ClassificationRow
                  classification={root}
                  isSelected={root.id === selectedId || (!isExpanded && isChildSelected)}
                  onToggleSelect={() => onToggleSelect(root.id)}
                />
              </Box>
              {isExpandLoading ? <CircularProgress size={14} sx={{ color: PRIMARY_TEAL }} /> : null}
              {root.hasChildren ? (
                <IconButton
                  aria-label={isExpanded ? `Collapse ${root.label}` : `Expand ${root.label}`}
                  size="small"
                  sx={{ padding: '2px' }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onToggleExpand(root.id);
                  }}
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      color: isExpanded ? NEUTRAL_600 : DARKER_GREEN,
                      transform: isExpanded ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s'
                    }}
                  />
                </IconButton>
              ) : null}
            </Box>

            {isExpanded
              ? (children ?? []).map((child) => (
                  <ClassificationRow
                    key={child.id}
                    indent
                    classification={child}
                    isSelected={child.id === selectedId}
                    onToggleSelect={() => onToggleSelect(child.id)}
                  />
                ))
              : null}
          </Box>
        );
      })}
    </Box>
  </Box>
);

export default ClassificationsPanel;
